import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { selectSlots, getCoursesWithBatches } from "services/api";
import { useAuth } from "stores/auth/auth-context";
import { ISection } from "services/api/packages/packageDetails.types";
import {
  IUseGetCoursesById,
  IUseGetCoursesByIdsProps
} from "./use-get-courses-by-ids.types";
import {
  IApiError,
  ISubmitSlotSelectionPayload
} from "services/api/api.types";

export const useGetCoursesById = ({
  router,
  onSuccess
}: IUseGetCoursesByIdsProps): IUseGetCoursesById => {
  const { user } = useAuth();
  const { userData } = user;

  const rawCourseIds = router.query["courseIds[]"];
  const courseIds = Array.isArray(rawCourseIds)
    ? rawCourseIds
    : rawCourseIds
    ? [rawCourseIds]
    : [];

  const [sectionsData, setSectionsData] = useState<ISection[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchCoursesWithBatches() {
      try {
        const response = await getCoursesWithBatches(
          courseIds.map(String),
          userData?.primary_user?.access_token || ""
        );
        const sections = response.data.sections.map((section) => ({
          ...section,
          slots: section.slots.map((slot) => ({
            ...slot,
            course: { id: section.course_id, name: section.title }
          }))
        }));
        setSectionsData(sections);
        onSuccess?.(sections);
        setIsLoading(false);
      } catch (err) {
        Sentry.captureException(err);
        router.push({ pathname: "/404" });
      }
    }

    if (router.isReady && courseIds.length) {
      fetchCoursesWithBatches();
    }
  }, [router.isReady, courseIds.join(","), userData?.primary_user]);

  const selectSlotsHandler = async (payload: ISubmitSlotSelectionPayload) => {
    try {
      await selectSlots(payload, userData?.primary_user.access_token);
      return { error: null };
    } catch (error) {
      Sentry.captureException(error);
      const { message } = error as IApiError;
      return { error: message };
    }
  };

  return {
    sectionsData,
    isLoading,
    selectSlots: selectSlotsHandler
  };
};
